import { useCallback, useMemo, useState } from 'react';

import type { IActiveFilterChip } from '../components/DataTable/DataTable.helpers';
import type { ISelectInputOption } from '../components/SelectInput/SelectInput.helpers';
import type { ITableFilterSelectInputProps } from '../components/TableFilter/TableFilter.helpers';

export interface ITableFilterField {
	key: string;
	label: string;
	options: ISelectInputOption[];
}

const EMPTY_OPTION: ISelectInputOption = { label: 'Selection', value: '' };

const withoutKey = (values: Record<string, string>, key: string): Record<string, string> =>
	Object.fromEntries(Object.entries(values).filter(([entryKey]) => entryKey !== key));

export const useTableFilters = (fields: ITableFilterField[], onChange?: (values: Record<string, string>) => void) => {
	const [values, setValues] = useState<Record<string, string>>({});

	const commit = useCallback(
		(next: Record<string, string>) => {
			setValues(next);
			onChange?.(next);
		},
		[onChange]
	);

	const selectField = useCallback(
		(key: string) => (option: ISelectInputOption) =>
			commit(option.value ? { ...values, [key]: String(option.value) } : withoutKey(values, key)),
		[commit, values]
	);

	const clearField = useCallback((key: string) => () => commit(withoutKey(values, key)), [commit, values]);

	const clearAll = useCallback(() => commit({}), [commit]);

	const filterItems = useMemo<ITableFilterSelectInputProps[]>(
		() =>
			fields.map((field) => ({
				label: field.label,
				defaultOption: EMPTY_OPTION,
				options: field.options,
				value: values[field.key] ?? '',
				onChange: selectField(field.key),
				onClear: clearField(field.key),
			})),
		[fields, values, selectField, clearField]
	);

	const activeFilters = useMemo<IActiveFilterChip[]>(
		() =>
			fields.reduce<IActiveFilterChip[]>((chips, field) => {
				const value = values[field.key];
				if (value) {
					chips.push({
						id: field.key,
						label: field.options.find((option) => String(option.value) === value)?.label ?? value,
						onRemove: clearField(field.key),
					});
				}
				return chips;
			}, []),
		[fields, values, clearField]
	);

	return {
		filterItems,
		activeFilters,
		filterCount: activeFilters.length,
		isFiltered: activeFilters.length > 0,
		clearAll,
		values,
	};
};
